// adelantarHuecos.js — «Rellenar huecos» ADELANTANDO prácticas en la app (14/10/2026, Wins #24f y Arribas #23m).
// Ficha: docs/features/autoescuela-adelantar-practicas-huecos.md
//
// El hueco liberado se ofrece también a alumnos que YA tienen una práctica más tarde: les llega practica_adelantar_crm_trial
// («Adelantar» / «Dejarla como está») y, si aceptan, SU práctica pasa al hueco (no se crea otra, el bono no cambia) y su
// hora vieja vuelve a ofrecerse. Aquí: los botones y lo que se le contesta (vista previa interactiva), la vista previa
// de la plantilla y la frase que resume la opción.
//
// ESPEJO del backend (lo fija __tests__/adelantarHuecos.mirror.test.js):
//   - TEXTOS_ADELANTO           ← aws_lambdas/c_offerPracticaSlot/adelantar.js (lo que contesta al pulsar «Adelantar»)
//   - BOTON_ADELANTO(_CA)       ← admin_scripts/add_autoescuela_adelantar_y_pago_practica_templates.js (botones de Meta)
//
// COPIA POR VALOR (fichero idéntico) en apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/adelantarHuecos.js —
// un test del simulador compara los dos ficheros.
import { vistaPlantilla } from "./plantillasAutoescuela";

const VAR = (clave) => `⟦${clave}⟧`;

export const BOTON_ADELANTO = Object.freeze({ adelantar: "Adelantar", dejar: "Dejarla como está" });
// Botones de la traducción catalana (mismo orden). Su payload es el de la oferta (practica_slot_*), no la etiqueta.
export const BOTON_ADELANTO_CA = Object.freeze({ "Avançar": "Adelantar", "Deixar-la com està": "Dejarla como está" });

/** ¿Es un botón de la oferta de adelantar (en castellano o en catalán)? */
export const esBotonAdelanto = (b) => b === BOTON_ADELANTO.adelantar || b === BOTON_ADELANTO.dejar || b in BOTON_ADELANTO_CA;
const aEs = (b) => BOTON_ADELANTO_CA[b] || b;

// Lo que contesta c_offerPracticaSlot al pulsar «Adelantar» (texto libre). Sin «¡» ni «¿» de apertura.
export const TEXTOS_ADELANTO = {
  es: {
    hecho: "Hecho. Tu práctica pasa al {nueva} a las {horaNueva}. Ya no tienes la del {vieja} a las {horaVieja}.",
    cambiada: "No hemos podido adelantar tu práctica porque ha cambiado desde que te avisamos. Tu práctica no se ha tocado.",
    yaNoLibre: "Esa hora ya no está libre. Tu práctica se queda como estaba.",
  },
  ca: {
    hecho: "Fet. La teva pràctica passa al {nueva} a les {horaNueva}. Ja no tens la del {vieja} a les {horaVieja}.",
    cambiada: "No hem pogut avançar la teva pràctica perquè ha canviat des que et vam avisar. La teva pràctica no s'ha tocat.",
    yaNoLibre: "Aquesta hora ja no està lliure. La teva pràctica es queda com estava.",
  },
};
// Otro alumno pulsó antes / se pasó el plazo: los mismos textos que la oferta de siempre (c_offerPracticaSlot TEXTOS).
const OTRO = {
  es: "Lo sentimos, otro alumno se ha quedado ya con esa práctica. Te avisaremos en cuanto se libere otra.",
  ca: "Ho sentim, un altre alumne ja s'ha quedat aquesta pràctica. T'avisarem tan bon punt se n'alliberi una altra.",
};
const PLAZO = {
  es: "Esa práctica ya no está disponible: el plazo para cogerla ha pasado. Te avisaremos en cuanto se libere otra.",
  ca: "Aquesta pràctica ja no està disponible: el termini per agafar-la ha passat. T'avisarem tan bon punt se n'alliberi una altra.",
};

export function textoAdelanto(lang, clave, vars = {}) {
  const t = TEXTOS_ADELANTO[lang === "ca" ? "ca" : "es"][clave] || "";
  return t.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ""));
}

const nota = (k, d) => ({ k, d });

/**
 * Lo que pasa al pulsar un botón de la oferta de adelantar (vista previa interactiva).
 * @param {string} boton
 * @param {object} o  { deNegocio, ca, resultado: "gana"|"otro"|"plazo", saltos }
 */
export function respuestaAdelanto(boton, { deNegocio = (t) => t, ca = false, resultado = "gana", saltos = 2 } = {}) {
  const l = ca ? "ca" : "es";
  const vacio = { mensajes: [], leido: false, url: false, crm: [] };
  if (aEs(boton) === BOTON_ADELANTO.dejar) {
    return { ...vacio, crm: [nota("crmAdelantoNo", "No se le contesta: su práctica se queda donde estaba y el hueco se sigue ofreciendo a los demás.")] };
  }
  if (resultado === "otro") {
    return { ...vacio, mensajes: [{ texto: deNegocio(OTRO[l]) }], crm: [nota("crmAdelantoOtro", "Otro alumno pulsó antes: su práctica no se mueve.")] };
  }
  if (resultado === "plazo") {
    return { ...vacio, mensajes: [{ texto: deNegocio(PLAZO[l]) }], crm: [nota("crmAdelantoPlazo", "Ya había pasado el tiempo para responder: su práctica no se mueve.")] };
  }
  const vars = l === "ca"
    ? { nueva: VAR("fechaLargaCa"), horaNueva: VAR("hora"), vieja: VAR("fechaSuyaCa"), horaVieja: VAR("horaSuya") }
    : { nueva: VAR("fechaLarga"), horaNueva: VAR("hora"), vieja: VAR("fechaSuya"), horaVieja: VAR("horaSuya") };
  return {
    ...vacio,
    mensajes: [{ texto: deNegocio(textoAdelanto(l, "hecho", vars)) }],
    crm: [
      nota("crmAdelantoMovida", "En el CRM: su práctica se MUEVE a esta hora (no se crea otra y su bono no cambia), con la traza «Adelantada a un hueco libre»."),
      Number(saltos) > 0
        ? nota("crmAdelantoCadena", `Su hora de antes queda libre y se ofrece igual a otros alumnos (como mucho ${saltos} ${Number(saltos) === 1 ? "vez" : "veces"} seguidas).`)
        : nota("crmAdelantoSinCadena", "Su hora de antes queda libre en Planificación (no se vuelve a ofrecer: «Saltos» está en 0)."),
    ],
  };
}

/** Vista previa de practica_adelantar_crm_trial (solo existe por el número compartido). */
export function previewPracticaAdelantar({ conext = true, businessName, alumnoName, fecha, hora, profesor, fechaSuya, horaSuya, idioma = "es", aMedida = null } = {}) {
  const ca = idioma === "ca" && conext !== false;
  const v = vistaPlantilla("practica_adelantar_crm_trial", {
    idioma: ca ? "ca" : "es", aMedida,
    vals: [
      businessName || "Tu autoescuela", alumnoName || (ca ? "Alumne" : "Alumno"),
      fecha || VAR(ca ? "fechaLargaCa" : "fechaLarga"), hora || VAR("hora"), profesor || VAR("profesor"),
      fechaSuya || VAR(ca ? "fechaSuyaCa" : "fechaSuya"), horaSuya || VAR("horaSuya"),
    ],
  });
  if (!v) return null;
  return {
    title: v.aMedida ? "Vista previa · Adelantar práctica · a medida" : "Vista previa · Adelantar práctica",
    message: v.message,
    ...(v.footer ? { footer: v.footer } : {}),
    ...(v.buttons ? { buttons: v.buttons } : {}),
  };
}

/** Una frase con la opción, para la nota de la vista previa («También a quien…»). null si está apagada. */
export function resumenAdelanto(cfg = {}) {
  if (cfg.advance_enabled !== true) return null;
  const dias = Number(cfg.advance_max_days) || 3;
  const prof = cfg.advance_same_instructor !== false ? " con el mismo profesor" : "";
  const prio = cfg.advance_priority === "adelantar" ? " Primero se ofrece a los que adelantan." : "";
  return `También a quien tiene una práctica${prof} en los ${dias} días siguientes, para adelantarla.${prio}`;
}
